import {
  Stack,
  Typography,
  Switch,
  Button,
  TableCell,
  Checkbox,
  TableRow,
  TableContainer,
  Table,
  TableHead,
  TablePagination,
  Pagination,
  Paper,
  TableSortLabel,
  SxProps,
  TableBody,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import {
  formatNumber,
  formatTwoDecimal,
} from "../../../../../../../../../utils/helpers";
import { useSelector } from "react-redux";
import { countNonNegativeInadgroup } from "../../../../../utilsSearchTerm/helperST";
import {
  setDefinitionModalSourceForIdenticalToExactKeyword,
  setIsDefinitionModalOpenForIdenticalToExactKeyword,
  setIsOpenModalOfAdgroupsForIdenticalToExactKeyword,
  setOtherNonNegativeAdgroupsForIdenticalToExactKeyword,
  setSelectedRowsForIdenticalToExactKeyword,
  setToggledNegativesForIdenticalToExactKeyword,
} from "../../../../../../../../../features/searchTermTabSlice";
import { Theme } from "@emotion/react";
import IdenticalToExactKeywordFilterCard from "./IdenticalToExactKeywordFilterCard";

const tableHeaders = [
  { id: 1, KEY: "search_term", label: "Search Term", sortable: true },
  { id: 2, KEY: "search_term_match_type", label: "Match Type", sortable: true },
  { id: 3, KEY: "keyword_name", label: "Keyword Name", sortable: true },
  {
    id: 4,
    KEY: "keyword_match_type",
    label: "Keyword Match Type",
    sortable: true,
  },
  { id: 5, KEY: "adgroup_name", label: "Adgroup Name", sortable: true },
  { id: 6, KEY: "campaign_name", label: "Campaign Name", sortable: true },
  { id: 7, KEY: "last_seen", label: "Last Seen", sortable: true },
  { id: 8, KEY: "impression_last_30_days", label: "IMP", sortable: true },
  { id: 9, KEY: "clicks_last_30_days", label: "Clicks", sortable: true },
  { id: 10, KEY: "cost_last_30_days", label: "Cost", sortable: true },
  { id: 11, KEY: "conversions_last_30_days", label: "CVR (%)", sortable: true },
  {
    id: 12,
    KEY: "non_negative_in_ad_group_name",
    label: "Non-negative adgroup",
    sortable: true,
  },
  {
    id: 13,
    KEY: "otherNonNegativeAdgroupsCount",
    label: "Other non-negative adgroups",
    sortable: true,
  },
  {
    id: 14,
    KEY: "Negative",
    label: "Negative",
    sortable: false,
    type: "custom", // for custom Autocomplete render
  },
];

const IdenticalToExactKeywordTable = () => {
  const dispatch = useDispatch();
  // const [searchParams, setSearchParams] = useSearchParams();
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "search_term", direction: "desc" });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    stIdenticalToExactKeyword,
    selectedCampaignForIdenticalToExactKeyword,
    selectedAdGroupForIdenticalToExactKeyword,
    selectedKeywordForIdenticalToExactKeyword,
    selectedSearchTermForIdenticalToExactKeyword,
    selectedRowsForIdenticalToExactKeyword,
    toggledNegativesForIdenticalToExactKeyword,
  } = useSelector((state: any) => state.searchTermTab);

  //-- Filtering Data
  const filteredRows = useMemo(() => {
    return (sortedData || []).filter((row: any) => {
      const campaignMatch =
        selectedCampaignForIdenticalToExactKeyword.length > 0
          ? selectedCampaignForIdenticalToExactKeyword.includes(
              row.campaign_name
            )
          : true;

      const adgroupMatch =
        selectedAdGroupForIdenticalToExactKeyword.length > 0
          ? selectedAdGroupForIdenticalToExactKeyword.includes(row.adgroup_name)
          : true;

      const keywordMatch =
        selectedKeywordForIdenticalToExactKeyword.length > 0
          ? selectedKeywordForIdenticalToExactKeyword.includes(row.keyword_name)
          : true;

      const searchMatch = selectedSearchTermForIdenticalToExactKeyword
        ? row.search_term
            ?.toLowerCase()
            .trim()
            .includes(
              selectedSearchTermForIdenticalToExactKeyword?.toLowerCase().trim()
            )
        : true;

      return campaignMatch && adgroupMatch && keywordMatch && searchMatch;
    });
  }, [
    sortedData,
    selectedCampaignForIdenticalToExactKeyword,
    selectedAdGroupForIdenticalToExactKeyword,
    selectedKeywordForIdenticalToExactKeyword,
    selectedSearchTermForIdenticalToExactKeyword,
  ]);
  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) return 0;

        let aValue = a[keytoSort];
        let bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  useEffect(() => {
    if (!stIdenticalToExactKeyword) return;
    const modifiedWithUniqueIds = stIdenticalToExactKeyword.map((row: any) => ({
      ...row,
      id: `${uuidv4()}`,
      otherNonNegativeAdgroupsCount: 0,
    }));
    // OtherKeywordsCount for ALL data BEFORE filtering
    const withNonNegativeAdgroupsCount = countNonNegativeInadgroup(
      modifiedWithUniqueIds
    );

    // Then sort the data with accurate counts
    const sortedRows = getSortedData(withNonNegativeAdgroupsCount || []);

    setSortedData(sortedRows);
  }, [stIdenticalToExactKeyword, getSortedData]);

  useEffect(() => {
    if (selectedRowIds.length === 0) return;

    const visibleSelectedIds = selectedRowIds.filter((id) =>
      filteredRows.some((r) => r.id === id)
    );

    if (visibleSelectedIds.length > 0) {
      if (visibleSelectedIds.length !== selectedRowIds.length) {
        setSelectedRowIds(visibleSelectedIds);
        const visibleSelectedRows = filteredRows.filter((r) =>
          visibleSelectedIds.includes(r.id)
        );
        dispatch(
          setSelectedRowsForIdenticalToExactKeyword(visibleSelectedRows)
        );
      }
    } else {
      setSelectedRowIds([]);
      dispatch(setSelectedRowsForIdenticalToExactKeyword([]));
    }
    // eslint-disable-next-line
  }, [filteredRows]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  //-- Function to handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = paginatedRows.map((row: any) => row.id);
      setSelectedRowIds(newSelected);
      dispatch(setSelectedRowsForIdenticalToExactKeyword(paginatedRows));
      dispatch(setToggledNegativesForIdenticalToExactKeyword([])); // clear negative definitions if selected
    } else {
      setSelectedRowIds([]);
      dispatch(setSelectedRowsForIdenticalToExactKeyword([]));
    }
  };

  //-- Function to handle row selection(check on single checkbox)
  const handleSelectRow = (id: any) => {
    const isSelected = selectedRowIds.includes(id);
    let newSelected: any[] = [];
    let updatedNegativeDefinitions = [
      ...toggledNegativesForIdenticalToExactKeyword,
    ];

    if (isSelected) {
      // Deselect row
      newSelected = selectedRowIds.filter((rowId: any) => rowId !== id);
    } else {
      // Select row → clear its negative definition(switch off)
      newSelected = [...selectedRowIds, id];
      updatedNegativeDefinitions =
        toggledNegativesForIdenticalToExactKeyword.filter(
          (item: any) => item.id !== id
        );
    }

    setSelectedRowIds(newSelected);

    // Update Redux (selected rows)
    const newSelectedRows = filteredRows.filter((row: any) =>
      newSelected.includes(row.id)
    );
    dispatch(setSelectedRowsForIdenticalToExactKeyword(newSelectedRows));

    // Update Redux (cleared definitions)
    dispatch(
      setToggledNegativesForIdenticalToExactKeyword(updatedNegativeDefinitions)
    );
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    // const updatedParams = new URLSearchParams(window.location.search);
    // updatedParams.set("page2", newPage.toString());
    // setSearchParams(updatedParams);
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForIdenticalToExactKeyword([]));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForIdenticalToExactKeyword([]));
  };

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;

    setSort({
      keytoSort: header.KEY,
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  //-- Function to handle toggle of negatives
  const handleToggleNegatives = (row: any) => {
    // 1st Check if row is already toggled
    const exists = toggledNegativesForIdenticalToExactKeyword.some(
      (item: any) => item.id === row.id
    );

    let updated: any[] = [];

    if (exists) {
      // Remove row if already toggled
      updated = toggledNegativesForIdenticalToExactKeyword.filter(
        (item: any) => item.id !== row.id
      );
    } else {
      // Add row if not toggled
      updated = [...toggledNegativesForIdenticalToExactKeyword, row];
    }

    dispatch(setToggledNegativesForIdenticalToExactKeyword(updated));
  };

  useEffect(() => {
    setPage(0);
    setRowsPerPage(10);
    // eslint-disable-next-line
  }, [
    selectedCampaignForIdenticalToExactKeyword,
    selectedAdGroupForIdenticalToExactKeyword,
    selectedKeywordForIdenticalToExactKeyword,
    selectedSearchTermForIdenticalToExactKeyword,
  ]);

  useEffect(() => {
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForIdenticalToExactKeyword([]));
    setPage(0);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedRowsForIdenticalToExactKeyword?.length === 0) {
      setSelectedRowIds([]);
    }
    // eslint-disable-next-line
  }, [selectedRowsForIdenticalToExactKeyword]);

  return (
    <>
      <>
        <IdenticalToExactKeywordFilterCard />

        {/* DataGrid */}
        <Stack flexDirection={"column"} alignItems={"flex-end"} gap={2}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              mt: 2,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 5, // a higher zIndex ensures it sits above table rows
                      backgroundColor: "background.paper",
                    }}
                  >
                    <Checkbox
                      data-tracker="564"
                      color="secondary"
                      indeterminate={
                        selectedRowIds.length > 0 &&
                        selectedRowIds.length < paginatedRows.length
                      }
                      checked={
                        paginatedRows.length > 0 &&
                        selectedRowIds.length === paginatedRows.length &&
                        selectedRowsForIdenticalToExactKeyword.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  {tableHeaders.map((header: any) => {
                    // Which columns should be sticky
                    const isLeftSticky = header.id === 1 || header.id === 2;
                    const isRightSticky =
                      header.id === tableHeaders[tableHeaders.length - 1].id; // last column

                    const leftOffsets: Record<number, number> = {
                      1: 52,
                      2: 162,
                    };

                    const cellSx = isLeftSticky
                      ? ({
                          position: "sticky",
                          left: leftOffsets[header.id] ?? 0,
                          zIndex: 10,
                          backgroundColor: "background.paper",
                          borderRight:
                            header.id === 2 ? "1px solid #e0e0e0" : undefined,
                        } as SxProps<Theme>)
                      : isRightSticky
                      ? ({
                          position: "sticky",
                          right: 0,
                          zIndex: 9,
                          backgroundColor: "background.paper",
                          borderLeft: "1px solid #e0e0e0",
                        } as SxProps<Theme>)
                      : ({} as SxProps<Theme>);

                    return (
                      <TableCell key={header.id} align="left" sx={cellSx}>
                        <TableSortLabel
                          active={sort.keytoSort === header.KEY}
                          direction={
                            sort.keytoSort === header.KEY
                              ? sort.direction
                              : "asc"
                          }
                          onClick={() => handleHeaderClick(header)}
                          disabled={!header.sortable}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {header.label}
                        </TableSortLabel>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row: any) => {
                    const isSelected = selectedRowIds.includes(row.id);
                    return (
                      <TableRow
                        hover
                        key={row.id}
                        selected={isSelected}
                        sx={{
                          backgroundColor: isSelected
                            ? "rgba(156, 39, 176, 0.08) !important"
                            : "inherit",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? ""
                              : "inherit !important",
                          },
                        }}
                      >
                        <TableCell
                          padding="checkbox"
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                            backgroundColor: isSelected
                              ? "#f0def3"
                              : "background.paper",
                          }}
                        >
                          <Checkbox
                            data-tracker="564"
                            color="secondary"
                            checked={isSelected}
                            onChange={() => handleSelectRow(row.id)}
                            // onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 52,
                            zIndex: 1,
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            backgroundColor: isSelected
                              ? "#f0def3"
                              : "background.paper",
                          }}
                        >
                          {row.search_term}
                        </TableCell>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 162,
                            zIndex: 1,
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            backgroundColor: isSelected
                              ? "#f0def3"
                              : "background.paper",
                            borderRight: "1px solid #e0e0e0",
                          }}
                        >
                          {row.search_term_match_type ?? ""}
                        </TableCell>
                        <TableCell>{row.keyword_name ?? ""}</TableCell>
                        <TableCell>{row.keyword_match_type ?? ""}</TableCell>
                        <TableCell>{row.adgroup_name ?? ""}</TableCell>
                        <TableCell>{row.campaign_name ?? ""}</TableCell>

                        <TableCell>
                          {row.last_seen
                            ? dayjs(row.last_seen).format("DD/MM/YYYY")
                            : ""}
                        </TableCell>
                        <TableCell>
                          {row.impression_last_30_days
                            ? formatNumber(row.impression_last_30_days)
                            : ""}
                        </TableCell>
                        <TableCell>
                          {row.clicks_last_30_days
                            ? formatNumber(row.clicks_last_30_days)
                            : ""}
                        </TableCell>
                        <TableCell>
                          {formatTwoDecimal(row.cost_last_30_days, "$")}
                        </TableCell>
                        <TableCell>
                          {formatTwoDecimal(row.conversions_last_30_days, "%")}
                        </TableCell>
                        <TableCell>
                          {row.non_negative_in_ad_group_name ?? ""}
                        </TableCell>
                        <TableCell>
                          <Typography
                            data-tracker="569"
                            variant="body2"
                            sx={{
                              fontSize: "0.99rem",
                              fontWeight: 500,
                              color:
                                row.otherNonNegativeAdgroupsCount > 0
                                  ? "purple"
                                  : "default",
                              cursor:
                                row.otherNonNegativeAdgroupsCount > 0
                                  ? "pointer"
                                  : "default",
                              textDecoration:
                                row.otherNonNegativeAdgroupsCount > 0
                                  ? "underline"
                                  : "none",
                              "&:hover": {
                                textDecoration:
                                  row.otherNonNegativeAdgroupsCount > 0
                                    ? "underline"
                                    : "none",
                              },
                            }}
                            onClick={
                              row.otherNonNegativeAdgroupsCount > 0
                                ? () => {
                                    dispatch(
                                      setIsOpenModalOfAdgroupsForIdenticalToExactKeyword(
                                        true
                                      )
                                    );
                                    dispatch(
                                      setOtherNonNegativeAdgroupsForIdenticalToExactKeyword(
                                        row.otherNonNegativeAdgroupsRows || []
                                      )
                                    );
                                  }
                                : undefined
                            }
                          >
                            {row.otherNonNegativeAdgroupsCount}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                            backgroundColor: isSelected
                              ? "#f0def3"
                              : "background.paper",
                            borderLeft: "1px solid #e0e0e0",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            // onClick={(e) => e.stopPropagation()}
                          >
                            <Switch
                              data-tracker="565"
                              color="success"
                              sx={{
                                "&.Mui-disabled": {
                                  cursor: "default",
                                },
                                "&.Mui-disabled .MuiSwitch-switchBase": {
                                  cursor: "default",
                                  pointerEvents: "none",
                                },
                                "&.Mui-disabled .MuiSwitch-thumb": {
                                  cursor: "default",
                                },
                                "&.Mui-disabled .MuiSwitch-track": {
                                  cursor: "default",
                                },
                              }}
                              checked={toggledNegativesForIdenticalToExactKeyword.some(
                                (item: any) => item.id === row.id
                              )}
                              disabled={selectedRowIds.includes(row.id)}
                              onClick={() => handleToggleNegatives(row)}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={tableHeaders.length + 1} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            data-tracker="567"
            variant="contained"
            color="secondary"
            endIcon={<SaveOutlinedIcon />}
            disabled={toggledNegativesForIdenticalToExactKeyword?.length === 0}
            onClick={() => {
              dispatch(
                setIsDefinitionModalOpenForIdenticalToExactKeyword(true)
              );
              dispatch(
                setDefinitionModalSourceForIdenticalToExactKeyword("saveButton")
              );
            }}
          >
            Save changes
          </Button>
        </Stack>
        {paginatedRows.length > 0 && (
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 1 }}
          >
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                  {
                    display: "none",
                  },
                ".MuiTablePagination-actions": { display: "none" },
                ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
              }}
            />

            <Pagination
              count={Math.ceil(filteredRows.length / rowsPerPage)}
              onChange={(_, value) => handleChangePage(null, value - 1)}
              page={page + 1}
              showFirstButton
              showLastButton
              color="secondary"
              size="small"
            />

            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              classes={{
                displayedRows: "hidden-displayed-rows", // Apply the custom class
              }}
              sx={{
                ".MuiTablePagination-actions": { display: "none" },
                ".MuiTablePagination-input": { marginRight: "15px" },
              }}
            />
          </Stack>
        )}
      </>
    </>
  );
};

export default IdenticalToExactKeywordTable;
